import { Component, computed, forwardRef, inject } from '@angular/core';
import { AdaptToSidebarVisibleDirective } from '../../../shared/directives/adapt-to-sidebar-visible.directive';
import {
  ParkourButtonComponent,
  ParkourDividerComponent,
  ParkourIconComponent,
  ParkourPageGradientComponent,
} from '@parkour/ui';
import { ParkourBackButtonDirective } from '../../../shared/directives/parkour-back-button.directive';
import { PageLayoutComponent } from '../../../shared/components/page-layout/page-layout.component';
import { ParkourRefreshDirective } from '../../../shared/directives/parkour-refresh-directive';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import { ParkourDataPage } from '../../../shared/parkour-data-page';
import { CmsContent, CmsInstructionContent, CmsPaginaDto } from 'parkour-web-app-dto';
import { map, Observable } from 'rxjs';
import { CmsService } from '../../../cms/cms.service';
import { PageContentDirective } from '../../../shared/directives/page-content.directive';
import { InstructionContentComponent } from '../../../cms/instruction-content/instruction-content.component';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';

type PageData = {
  doelenContent: CmsInstructionContent[];
  verhaalContent: CmsInstructionContent[];
  teamContent: CmsInstructionContent[];
  profielContent: CmsInstructionContent[];
  berichtenContent: CmsInstructionContent[];
  ondersteuningContent: CmsInstructionContent[];
};

@Component({
  templateUrl: './aan-de-slag.page.html',
  standalone: true,
  imports: [
    AdaptToSidebarVisibleDirective,
    ParkourBackButtonDirective,
    ParkourPageGradientComponent,
    ParkourDividerComponent,
    ParkourButtonComponent,
    PageLayoutComponent,
    ParkourRefreshDirective,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonBackButton,
    IonTitle,
    IonContent,
    IonRefresher,
    IonRefresherContent,
    forwardRef(() => PageContentDirective),
    InstructionContentComponent,
    ParkourIconComponent,
    TranslatePipe,
    AsyncPipe,
    NgTemplateOutlet,
  ],
})
export class AanDeSlagPage extends ParkourDataPage<PageData> implements ViewWillEnter {
  private readonly cmsService = inject(CmsService);
  private readonly route = inject(ActivatedRoute);
  private readonly queryParams = toSignal(this.route.queryParamMap);
  protected readonly initiallyActiveItem = computed(
    () => this.queryParams()?.get('instructionReference') ?? '',
  );

  override retrieveMainData(): Observable<PageData> {
    return this.cmsService.getAanDeSlagPagina().pipe(
      map((pagina) => this.filterInstructionContent(pagina)),
      map((instructions: CmsInstructionContent[]) => {
        const doelenContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'doelen',
        );
        const verhaalContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'verhaal',
        );
        const teamContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'team',
        );
        const profielContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'profiel',
        );
        const berichtenContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'berichten',
        );
        const ondersteuningContent = instructions.filter(
          (instruction) => instruction.category.toLowerCase() === 'ondersteuning',
        );
        return {
          doelenContent,
          verhaalContent,
          teamContent,
          profielContent,
          berichtenContent,
          ondersteuningContent,
        };
      }),
    );
  }

  private filterInstructionContent(pagina: CmsPaginaDto): CmsInstructionContent[] {
    const predicate = (content: CmsContent): content is CmsInstructionContent =>
      content.type === 'instruction';
    return pagina.content.filter<CmsInstructionContent>(predicate);
  }
}
